import { useCallback, useEffect, useState } from "react";
import { Bookmark, BookmarkPlus, Loader2, Pencil, Play, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtTime } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

export const bookmarkEvent = "audiobook-bookmarks-changed";

/* Drop a bookmark (with optional note) at the current moment of the playing audiobook. */
export const BookmarkButton = ({ tone = "light" }) => {
  const p = usePlayer();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const t = p.current;
  const dark = tone === "dark";
  if (!t?.is_audiobook) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/audiobooks/${t.album_id}/bookmarks`, { track_id: t.id, position: p.time, note });
      window.dispatchEvent(new Event(bookmarkEvent));
      toast.success(`Bookmarked at ${fmtTime(p.time)}`);
      setOpen(false); setNote("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} title="Add bookmark" aria-label="Add bookmark" data-testid="audiobook-bookmark-add"
        className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${dark ? "text-zinc-300 hover:bg-white/10 hover:text-white" : "text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900"} ${open ? (dark ? "bg-white/10" : "bg-zinc-200") : ""}`}>
        <BookmarkPlus size={16} />
      </button>
      {open && (
        <div className="absolute bottom-10 left-1/2 z-50 w-72 -translate-x-1/2 rounded-xl border border-zinc-200 bg-white p-3 text-left shadow-xl" data-testid="audiobook-bookmark-popover">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Bookmark · {fmtTime(p.time)}</span>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-zinc-400 hover:text-zinc-900"><X size={13} /></button>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} autoFocus placeholder="Add a short note (optional)" data-testid="audiobook-bookmark-note"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } }}
            className="w-full resize-none rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs text-zinc-900 focus:border-zinc-900 focus:outline-none" />
          <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-400">
            <span>{note.length}/300</span>
            <button onClick={save} disabled={busy} data-testid="audiobook-bookmark-save" className="flex h-7 items-center gap-1.5 rounded-full bg-zinc-900 px-3 text-xs font-medium text-white disabled:opacity-50">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Bookmark size={11} />}Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* Bookmarks of one book: jump back, edit note, delete. `onJump(trackId, position)` starts playback there. */
export const BookmarkList = ({ albumId, onJump }) => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // { id, note }
  const load = useCallback(() => api.get(`/audiobooks/${albumId}/bookmarks`).then((r) => setItems(r.data.items)).catch(() => setItems([])), [albumId]);
  useEffect(() => { load(); window.addEventListener(bookmarkEvent, load); return () => window.removeEventListener(bookmarkEvent, load); }, [load]);
  const remove = async (b) => {
    try { await api.delete(`/audiobooks/${albumId}/bookmarks/${b.id}`); setItems((xs) => xs.filter((x) => x.id !== b.id)); toast.success("Bookmark removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveNote = async () => {
    try { await api.patch(`/audiobooks/${albumId}/bookmarks/${editing.id}`, { note: editing.note }); setItems((xs) => xs.map((x) => (x.id === editing.id ? { ...x, note: editing.note } : x))); setEditing(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!items?.length) return null;
  return (
    <section className="mt-10" data-testid="audiobook-bookmarks">
      <h2 className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Bookmarks · {items.length}</h2>
      <ul className="divide-y divide-zinc-200/70 rounded-xl border border-zinc-200 bg-white/70">
        {items.map((b) => (
          <li key={b.id} className="group flex items-start gap-3 px-4 py-3" data-testid={`bookmark-${b.id}`}>
            <button onClick={() => onJump(b.track_id, b.position)} data-testid={`bookmark-jump-${b.id}`} title="Jump here"
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white transition-transform hover:scale-105"><Play size={11} fill="currentColor" className="ml-0.5" /></button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] text-zinc-500">{b.chapter_title ? `${b.chapter_title} · ` : ""}<span className="font-mono">{fmtTime(b.position)}</span></p>
              {editing?.id === b.id ? (
                <div className="mt-1 flex items-center gap-2">
                  <input value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value.slice(0, 300) })} autoFocus data-testid={`bookmark-note-input-${b.id}`}
                    onKeyDown={(e) => { if (e.key === "Enter") saveNote(); if (e.key === "Escape") setEditing(null); }}
                    className="h-7 flex-1 rounded-md border border-zinc-300 bg-white px-2 text-xs focus:border-zinc-900 focus:outline-none" />
                  <button onClick={saveNote} data-testid={`bookmark-note-save-${b.id}`} className="h-7 rounded-full bg-zinc-900 px-3 text-xs text-white">Save</button>
                  <button onClick={() => setEditing(null)} className="text-xs text-zinc-500">Cancel</button>
                </div>
              ) : (
                <p className={`mt-0.5 text-sm ${b.note ? "text-zinc-900" : "italic text-zinc-400"}`} data-testid={`bookmark-note-${b.id}`}>{b.note || "No note"}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              <button onClick={() => setEditing({ id: b.id, note: b.note || "" })} data-testid={`bookmark-edit-${b.id}`} aria-label="Edit note" className="rounded p-1 text-zinc-400 hover:text-zinc-900"><Pencil size={13} /></button>
              <button onClick={() => remove(b)} data-testid={`bookmark-delete-${b.id}`} aria-label="Delete bookmark" className="rounded p-1 text-zinc-400 hover:text-rose-600"><Trash2 size={13} /></button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
